/**
 * PayrollCorrectionPanel — the CWG2 §1.18 CORRECTION action on the pay-run
 * screen: the preview-then-commit gate over POST /v1/payroll/periods/restate
 * and its dry run (PR 175), targeting ONE existing row.
 *
 * WHY THIS GATE IS STRICTER THAN THE RECORD GATE. Recording writes a figure
 * where none stood; correcting DISPLACES a standing one. The old row is never
 * deleted — it is stamped superseded and a replacement is inserted — but there
 * is no un-supersede: no route, no command, no grant. A correction is
 * therefore permanent in the only sense that matters to an operator, and the
 * commit is deliberately not a bare button sitting next to a preview.
 *
 * THE CONFIRMATION IS A TYPED WORD: the operator types "supersede" to arm the
 * commit. Chosen over a checkbox or a plain second click because it is the
 * only mechanism that cannot be satisfied by muscle memory aimed at where the
 * button was — it requires reading the word, and the word names the act rather
 * than describing a button ("confirm", "yes"). The trade-off is honest: a
 * fixed word can become rote with repetition. What defends against correcting
 * the WRONG row is not the word but the panel itself — the target's identity
 * and its current figures sit beside the replacement, and neither can be seen
 * without previewing first. The typed word defends against the other mistake:
 * committing an intent the operator has not finished forming.
 *
 * ENTRY IS ALWAYS A ROW. This panel takes the pay-run row being corrected and
 * never a picker: a correction targets one existing period, and a global form
 * would invite naming a period that does not exist — the server's own named
 * 404 ("a period that has never been recorded is recorded, not restated")
 * exists precisely because that mistake is reachable.
 *
 * NO regularPayday FIELD, deliberately. The replacement inherits the
 * superseded row's payday: the period key names the target, and a payday
 * implying a different period would contradict it. The server refuses the
 * field with a named 400, so offering one would manufacture an error the form
 * can prevent. The panel states the inheritance instead.
 *
 * WHAT THE OPERATOR CANNOT SEE AFTER COMMITTING (recon finding, stated because
 * it shapes this design): NO read route returns superseded rows. The pay-run
 * and employee-series reads both come through payroll_period_derived_ytd,
 * whose definition filters `superseded_by IS NULL`. So once a correction
 * commits, the UI can show THAT a row was corrected — the replacement carries
 * `supersedes` on the wire — but the OLD figures become unreachable from the
 * browser. This preview is the only place they are ever shown, which is why it
 * renders them beside the new ones rather than leaving the operator to
 * remember. Retrieving chain history needs a route that does not exist; this
 * slice does not invent one.
 *
 * EVERY FIGURE COMES FROM THE SERVER, as on the record gate: the preview is
 * the identical transaction — stamp, insert, series-guard judgement — rolled
 * back instead of committed. Nothing here is computed in the browser.
 */
import { useState } from 'react'
import { Check, Eye, FileText, X } from 'lucide-react'
import { previewRestatePayrollPeriod, restatePayrollPeriod } from '../../api'
import type {
  PayrollLedgerRow, PayrollPeriodResult, RestatePayrollPeriodPreview, RestatePayrollPeriodRequest,
  Payslip,
} from '../../types'
import { ApiError, apiErrorMessage } from '../../../../api/client'
import { useAsync, fetchPayslips } from '../../api'
import PayslipViewer from './PayslipViewer'
import { money, signedMoney, penceCell } from '../../../../lib/money'
import { DeductionList, DeductionRowsEditor } from './DeductionFields'
import {
  deductionsTotalPence, draftFromDeduction, draftsToDeductions, draftsValid,
  type DeductionDraft,
} from './deductions'
import { HoursList, HoursSection } from './HoursFields'
import { draftFromHours, draftsToHours, type HoursDraft } from './hours'

const inputCls = 'w-full h-10 px-3 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent'
const labelCls = 'block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1'

const BASIS_LABEL: Record<string, string> = {
  cumulative: 'Cumulative', week1_month1: 'W1/M1',
}

/** The word that arms the commit — see the header comment. Compared
 *  case-insensitively and trimmed: the point is deliberate reading, not typing
 *  accuracy. */
const CONFIRM_WORD = 'supersede'

/** A successful preview and the commit key minted for it. They live and die
 *  together: dropping this state is exactly what invalidate-on-edit means. */
type Previewed = {
  preview: RestatePayrollPeriodPreview
  idempotencyKey: string
}

/** What the last preview attempt produced. A REFUSAL is not an error in the
 *  transient sense — the server judged this correction impossible (the series
 *  guard's 422, or nothing to restate) and would judge the commit the same
 *  way. It is rendered as the outcome, and the commit stays shut. */
type Outcome =
  | { kind: 'previewed'; value: Previewed }
  | { kind: 'refused'; message: string }
  | { kind: 'error'; message: string }

function Figure({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted">{label}</div>
      <div className={`text-13 tabular-nums ${muted ? 'text-ink-secondary' : 'text-ink-primary font-medium'}`}>{value}</div>
    </div>
  )
}

/** One row of the old/new comparison. */
// The three cells sit in a `display: contents` wrapper rather than a fragment:
// the grid still lays them out as one row of the parent, and the wrapper gives
// the pair a single element — so "this figure's before and after" is one thing
// in the DOM rather than three siblings a reader has to re-associate.
function Compare({ label, before, after }: { label: string; before: string; after: string }) {
  const changed = before !== after
  return (
    <div className="contents">
      <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted self-center">{label}</div>
      <div className="text-13 tabular-nums text-ink-secondary">{before}</div>
      <div className={`text-13 tabular-nums ${changed ? 'text-ink-primary font-semibold' : 'text-ink-secondary'}`}>{after}</div>
    </div>
  )
}

/** The figures compared side by side, in the pay-run table's own order. Each
 *  entry reads BOTH rows through the same formatter, so "absent" and "zero"
 *  stay distinguishable on both sides (an NI-only row's PAYE cell is an em
 *  dash on the left and on the right; a real £0.00 is a real zero on both). */
function comparisons(current: PayrollPeriodResult, next: PayrollPeriodResult) {
  return [
    { label: 'Gross', before: signedMoney(current.grossPence), after: signedMoney(next.grossPence) },
    { label: 'PBIK', before: signedMoney(current.pbikPence), after: signedMoney(next.pbikPence) },
    { label: 'Employee NI', before: penceCell(current.niEmployeePence), after: penceCell(next.niEmployeePence) },
    { label: 'Employer NI', before: penceCell(current.niEmployerPence), after: penceCell(next.niEmployerPence) },
    { label: 'PAYE tax', before: penceCell(current.payeTaxPence), after: penceCell(next.payeTaxPence) },
    { label: 'Student loan', before: penceCell(current.studentLoanPence), after: penceCell(next.studentLoanPence) },
    { label: 'PGL', before: penceCell(current.postgraduateLoanPence), after: penceCell(next.postgraduateLoanPence) },
    { label: 'Tax code', before: current.taxCode ?? '—', after: next.taxCode ?? '—' },
    { label: 'Basis', before: BASIS_LABEL[current.taxBasis] ?? current.taxBasis, after: BASIS_LABEL[next.taxBasis] ?? next.taxBasis },
    { label: 'Payment date', before: current.paymentDate.slice(0, 10), after: next.paymentDate.slice(0, 10) },
  ]
}

export default function PayrollCorrectionPanel({ row: target, onCorrected, onClose }: {
  /** The pay-run row being corrected. A correction always targets an existing
   *  row — see the header comment. */
  row: PayrollLedgerRow
  /** Called with the committed replacement so the page can refetch the pay run
   *  and show it in place of the row it superseded. */
  onCorrected: (replacement: PayrollPeriodResult) => void
  onClose: () => void
}) {
  // Prefilled from the row being corrected: the operator is editing an
  // existing figure, not entering a new one, and the comparison below only
  // reads as a correction if it starts from what stands today. changeReason is
  // NOT prefilled — it justifies THIS correction, not the original write.
  const [gross, setGross] = useState(String(target.grossPence / 100))
  const [pbik, setPbik] = useState(String(target.pbikPence / 100))
  const [paymentDate, setPaymentDate] = useState(target.paymentDate.slice(0, 10))
  const [changeReason, setChangeReason] = useState('')
  const [confirmText, setConfirmText] = useState('')
  // ── §D: THIS PANEL IS THE "PERMANENTLY RETRIEVABLE" SURFACE ───────────────
  // docs/payslip-concept.md §D.3 requires a superseded statement to stay
  // retrievable forever. Nowhere else can reach one: the pay-run and
  // employee-series reads both come through payroll_period_derived_ytd, whose
  // definition filters `superseded_by IS NULL`, so a superseded ROW vanishes
  // from every ledger surface the moment it is superseded — the finding this
  // panel's header comment already records.
  //
  // The PAYSLIP list route is different, and deliberately so: it joins the BARE
  // payroll_period_results table with no status predicate, and is queryable by
  // periodResultId. So a document bound to a superseded version stays readable
  // even though its row does not. That is what makes this panel able to keep
  // the promise: it is opened on the row that a correction is ABOUT to
  // supersede, and it is therefore the last surface on which that row's
  // statement is reachable by a human.
  const { data: targetPayslips } = useAsync(
    () => fetchPayslips({ periodResultId: target.id }), [target.id])
  const issuedPayslip: Payslip | undefined = targetPayslips?.[0]
  const [viewingPayslip, setViewingPayslip] = useState<Payslip | null>(null)

  // ── THE TRI-STATE ─────────────────────────────────────────────────────────
  //
  // The wire distinguishes three instructions where a form naturally offers
  // two: omitted = INHERIT, [] = explicitly NONE, [ … ] = REPLACE wholesale.
  // A form has no "omitted", so the third state has to be manufactured — and
  // the only honest way to manufacture it is to track whether the operator
  // TOUCHED the list at all.
  //
  //   pre-populated from the superseded row, labelled as inherited
  //   untouched            → the field is OMITTED     (inherit)
  //   edited in any way    → the full current list is SENT (replace)
  //   every row removed    → []                        (explicitly none)
  //
  // Pre-populating is what makes the mapping safe. The alternative — an empty
  // editor meaning "inherit" — would make "inherit three deductions" and
  // "clear all deductions" look IDENTICAL on screen, which is exactly the
  // confusion the tri-state exists to prevent. Here they never look alike: the
  // untouched state shows the inherited lines with an "inherited" label, and
  // the cleared state shows an explicit sentence saying they will be removed.
  //
  // `touched` is deliberately NOT computed by diffing drafts against the
  // original. A diff would call an edit-and-undo "untouched" and send OMITTED —
  // which happens to be the same outcome here, but only by luck: it would also
  // mean the operator's explicit re-affirmation of the list silently became an
  // inherit, and the two stop being equivalent the moment inheritance resolves
  // to something other than what was on screen (a chained correction). Tracking
  // the ACT rather than the VALUE keeps the sent instruction equal to the
  // instruction the operator gave.
  const [deductions, setDeductions] = useState<DeductionDraft[]>(
    () => target.deductions.map(draftFromDeduction)
  )
  const [deductionsTouched, setDeductionsTouched] = useState(false)

  // ── s.8(2)(e) hours: the same tri-state, gated by the PAYDAY'S declaration
  // Pre-populated from the row being corrected, exactly as deductions are, so
  // "inherit these" and "clear these" can never look alike.
  //
  // THE PRE-PREVIEW SIGNAL is the row itself: PayrollLedgerRow carries `hours`
  // only when the worker was time-varying on that payday, so its presence IS
  // the declaration as the read path resolved it. The PREVIEW then overrides —
  // and on a CROSS-DECLARATION correction the two legitimately differ, because
  // the preview resolves the profile as the table stands NOW for that payday.
  // That difference is the feature, not a glitch.
  const [hoursRows, setHoursRows] = useState<HoursDraft[]>(
    () => (target.hours ?? []).map(draftFromHours),
  )
  const [hoursTouched, setHoursTouched] = useState(false)

  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null)
  const [committed, setCommitted] = useState<{ row: PayrollPeriodResult; replayed: boolean } | null>(null)
  // A FAILED COMMIT IS NOT A FAILED PREVIEW, so it gets its own state. Folding
  // it into `outcome` would discard the preview and its key on any commit
  // error — and the retry would then have to re-preview, minting a NEW key,
  // which is exactly how a retry writes a second row. The key must survive
  // everything except an edit.
  const [commitError, setCommitError] = useState<string | null>(null)

  // THE ANTI-DRIFT RULE, one place: every input change discards the preview,
  // its commit key AND the typed confirmation. The confirmation belongs to the
  // previewed intent exactly as the key does — leaving it armed across an edit
  // would let a figure the operator never previewed ride a confirmation they
  // gave for one they did.
  function edit<T>(setter: (v: T) => void): (v: T) => void {
    return (v: T) => {
      setOutcome(null)
      setCommitted(null)
      setCommitError(null)
      setConfirmText('')
      setter(v)
    }
  }

  const grossNum = Number(gross)
  const pbikNum = Number(pbik)
  const inputsValid =
    gross !== '' && Number.isFinite(grossNum) && grossNum >= 0 &&
    (pbik === '' || (Number.isFinite(pbikNum) && pbikNum >= 0)) &&
    changeReason.trim() !== '' &&
    draftsValid(deductions)

  function requestBody(): RestatePayrollPeriodRequest {
    return {
      employeeId: target.employeeId,
      taxYear: target.taxYear,
      periodNumber: target.periodNumber,
      ...(paymentDate !== '' ? { paymentDate } : {}),
      grossPence: Math.round(grossNum * 100),
      ...(pbik !== '' ? { pbikPence: Math.round(pbikNum * 100) } : {}),
      changeReason: changeReason.trim(),
      // THE TRI-STATE ON THE WIRE. Conditional spread, never
      // `deductions: touched ? list : undefined` — an explicit undefined is
      // dropped by JSON.stringify today, but it makes the OMITTED case depend
      // on a serialiser detail rather than on the body's shape, and any future
      // code that inspects the object sees a key that is not supposed to be
      // there. Untouched omits the key entirely; touched sends the full list,
      // which is [] when every row has been removed.
      ...(deductionsTouched ? { deductions: draftsToDeductions(deductions) } : {}),
      // The tri-state, conditional-spread for the same reason: untouched
      // OMITS the key (inherit), touched sends the full list, which is [] when
      // every row has been removed. Never sent at all for a worker the
      // payday's declaration says is not time-varying — the server refuses a
      // supplied list by name, and [] counts as supplied.
      ...(flagTrue && hoursTouched ? { hours: draftsToHours(hoursRows) } : {}),
    }
  }

  // Every deduction mutation is an edit like any other (invalidate-on-edit)
  // AND records the ACT of touching, which is what turns OMITTED into REPLACE.
  function editDeductions(next: DeductionDraft[]) {
    setDeductionsTouched(true)
    edit(setDeductions)(next)
  }

  async function runPreview() {
    setBusy('preview')
    setOutcome(null)
    setCommitError(null)
    try {
      const preview = await previewRestatePayrollPeriod(requestBody())
      setOutcome({ kind: 'previewed', value: { preview, idempotencyKey: crypto.randomUUID() } })
    } catch (e) {
      // A 422 (the series guard) or a 404 (nothing recorded for this period)
      // is a JUDGEMENT, not a failure: the commit would answer identically, so
      // it is rendered as the outcome rather than as something to retry.
      const judged = e instanceof ApiError && (e.status === 422 || e.status === 404 || e.status === 409)
      const message = apiErrorMessage(e, 'Previewing this correction failed')
      setOutcome({ kind: judged ? 'refused' : 'error', message })
    } finally {
      setBusy(null)
    }
  }

  async function commit() {
    if (outcome?.kind !== 'previewed' || outcome.value.preview.alreadyRestated) return
    setBusy('commit')
    setCommitError(null)
    try {
      const result = await restatePayrollPeriod(requestBody(), outcome.value.idempotencyKey)
      setCommitted(result)
      onCorrected(result.row)
    } catch (e) {
      // The preview and its key stay put — see commitError's declaration.
      setCommitError(apiErrorMessage(e, 'Correcting this period failed'))
    } finally {
      setBusy(null)
    }
  }

  const previewed = outcome?.kind === 'previewed' ? outcome.value : null
  const preview = previewed?.preview

  // The declaration the correction is judged against: the preview's when one
  // exists (authoritative — it is what the restatement resolved), else the
  // row's own hours presence.
  const flagTrue = preview
    ? preview.payVariesByReferenceToTimeWorked
    : target.hours !== undefined

  function editHours(next: HoursDraft[]) {
    setHoursTouched(true)
    edit(setHoursRows)(next)
  }
  const alreadyRestated = preview?.alreadyRestated === true
  const confirmed = confirmText.trim().toLowerCase() === CONFIRM_WORD
  const commitDisabled = !previewed || alreadyRestated || !confirmed || busy !== null || committed !== null

  const changes = preview ? comparisons(preview.current, preview.row).filter(c => c.before !== c.after) : []

  return (
    <div className="k-card">
      <div className="p-4 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">
              Correct period {target.periodNumber} — {target.fullName}
            </div>
            <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
              A correction supersedes the current row and replaces it with a recomputed one. The old
              row is kept as history but stops being the period’s figure, and this cannot be undone.
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* The row's issued statement, if it has one. ABSENCE when it does not
            — not a disabled control: a greyed "view payslip" on a row that
            never had one invites the reader to think a document exists and is
            merely unavailable, which is the opposite of true. No ISSUE action
            appears here under any condition; issuing targets current rows and
            this row is about to stop being one. */}
        {issuedPayslip && !viewingPayslip && (
          <div className="flex items-center justify-between gap-3 rounded-tile border border-edge-control bg-surface-control px-3 py-2">
            <div className="text-12 text-ink-body leading-relaxed">
              A payslip for this period was issued on{' '}
              {new Date(issuedPayslip.issuedAt).toLocaleDateString('en-GB')}. It stays retrievable
              after this correction — the statement the worker was given is kept exactly as issued.
            </div>
            <button onClick={() => setViewingPayslip(issuedPayslip)}
              data-testid="correction-payslip-retrieve"
              aria-label={`View the payslip issued for period ${target.periodNumber}`}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-11 font-semibold rounded-control border border-edge-control bg-surface-nav-hover text-ink-body transition-colors hover:opacity-90 whitespace-nowrap flex-shrink-0">
              <FileText size={12} /> View payslip
            </button>
          </div>
        )}

        {viewingPayslip && (
          <div className="rounded-tile border border-edge-control p-3">
            <PayslipViewer payslip={viewingPayslip} onClose={() => setViewingPayslip(null)} />
          </div>
        )}

        {/* ── Step 1: the new inputs ─────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="cor-gross" className={labelCls}>Corrected gross (£)</label>
            <input id="cor-gross" type="number" min={0} step={0.01} value={gross}
              onChange={e => edit(setGross)(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="cor-pbik" className={labelCls}>
              PBIK (£) <span className="font-normal normal-case tracking-normal text-ink-faint">(optional)</span>
            </label>
            <input id="cor-pbik" type="number" min={0} step={0.01} value={pbik}
              onChange={e => edit(setPbik)(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="cor-payment" className={labelCls}>
              Payment date <span className="font-normal normal-case tracking-normal text-ink-faint">(optional)</span>
            </label>
            <input id="cor-payment" type="date" value={paymentDate}
              onChange={e => edit(setPaymentDate)(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="cor-reason" className={labelCls}>Reason for the correction</label>
            <input id="cor-reason" value={changeReason}
              onChange={e => edit(setChangeReason)(e.target.value)}
              placeholder="Gross was overstated — true figure is…" className={inputCls} />
          </div>
        </div>

        {/* ── Deductions: the tri-state made visible ─────────────────────── */}
        <div className="flex flex-col gap-2 pt-3 border-t border-edge-row-faint">
          <div>
            <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted">
              Deductions{' '}
              {!deductionsTouched && (
                <span className="font-normal normal-case tracking-normal text-ink-secondary">— inherited</span>
              )}
            </div>
            {/* The three states never look alike — that is the whole point of
                pre-populating. Each sentence names what WILL BE SENT. */}
            {!deductionsTouched ? (
              <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
                {target.deductions.length === 0
                  ? 'The current row has no deductions, and the replacement will inherit that. Add one to change it.'
                  : `Left as they are, the replacement inherits these ${target.deductions.length} deduction${target.deductions.length !== 1 ? 's' : ''} from the row being superseded. Editing any of them replaces the whole list instead.`}
              </div>
            ) : deductions.length === 0 ? (
              <div className="text-12 text-ink-primary mt-0.5 leading-relaxed font-medium">
                All deductions will be REMOVED. The replacement will carry none — this is different
                from leaving them alone, which would inherit them.
              </div>
            ) : (
              <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
                These {deductions.length} deduction{deductions.length !== 1 ? 's' : ''} REPLACE the
                superseded row's list wholesale — the replacement carries exactly what is here,
                nothing is merged.
              </div>
            )}
          </div>
          <DeductionRowsEditor idPrefix="cor-ded" drafts={deductions}
            onChange={editDeductions} disabled={busy !== null} />
        </div>

        {/* ── s.8(2)(e) hours, the same tri-state, gated by the PAYDAY'S
            declaration. ABSENT for a worker the declaration says is not
            time-varying — including the CROSS-DECLARATION case where the row
            HAS hours but the worker has since been re-declared salaried over
            that payday: the section disappears and the correction simply
            drops them, which is what the server does too. */}
        {flagTrue && (
          <div className="flex flex-col gap-2 pt-3 border-t border-edge-row-faint">
            <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted">
              Hours{' '}
              {!hoursTouched && (
                <span className="font-normal normal-case tracking-normal text-ink-secondary">— inherited</span>
              )}
            </div>
            {!hoursTouched ? (
              <div className="text-12 text-ink-muted leading-relaxed">
                {hoursRows.length === 0
                  ? 'The row being corrected records no hours, and the replacement will inherit that. Edit to change it.'
                  : `Left as they are, the replacement inherits these ${hoursRows.length} line${hoursRows.length !== 1 ? 's' : ''}. Editing any of them replaces the whole list.`}
              </div>
            ) : hoursRows.length === 0 ? (
              <div className="text-12 text-ink-primary leading-relaxed font-medium">
                All hours will be REMOVED — the replacement records an explicit “no variable hours
                this period”. That is different from leaving them alone, which would inherit them.
              </div>
            ) : (
              <div className="text-12 text-ink-muted leading-relaxed">
                These {hoursRows.length} line{hoursRows.length !== 1 ? 's' : ''} REPLACE the
                superseded row’s hours wholesale — nothing is merged.
              </div>
            )}
            <HoursSection idPrefix="cor-hrs" answer={hoursRows.length > 0 ? 'rows' : 'none'}
              onAnswer={a => editHours(a === 'none' ? [] : hoursRows)}
              drafts={hoursRows} onChange={editHours} disabled={busy !== null} />
          </div>
        )}
        {deductionsTouched && (
          <div className="text-12 text-ink-muted -mt-1">
            Total to be deducted: <span className="tabular-nums font-medium text-ink-primary">
              {money(deductionsTotalPence(draftsToDeductions(deductions.filter(d => Number.isFinite(Number(d.amount)) && d.amount !== ''))) / 100)}
            </span>
          </div>
        )}

        {/* The payday is NOT offered: the replacement inherits it. Saying so
            beats a field the server refuses by name. */}
        <div className="text-12 text-ink-muted leading-relaxed">
          The regular payday stays {target.regularPayday.slice(0, 10)} — a correction restates the
          period it names, so the replacement inherits the recorded payday. Moving a payment to a
          different period is not a correction.
        </div>

        <div className="flex items-center gap-2">
          <button onClick={runPreview} disabled={!inputsValid || busy !== null}
            className="k-btn-secondary k-btn-secondary--flex disabled:opacity-40 disabled:cursor-not-allowed">
            <Eye size={14} /> {busy === 'preview' ? 'Previewing…' : 'Preview correction'}
          </button>
          {!outcome && <span className="text-12 text-ink-muted">Preview before correcting.</span>}
        </div>

        {(outcome?.kind === 'error' || commitError) && (
          <div className="text-13 text-danger leading-relaxed" role="alert">
            {commitError ?? (outcome?.kind === 'error' ? outcome.message : null)}
          </div>
        )}

        {/* ── Step 3: a refusal the commit would hit, surfaced BEFORE it ──── */}
        {outcome?.kind === 'refused' && (
          <div className="flex flex-col gap-1 pt-3 border-t border-edge-row-faint" role="alert">
            <div className="text-13 font-semibold text-ink-primary">This correction cannot be made</div>
            <div className="text-13 text-danger leading-relaxed">{outcome.message}</div>
            <div className="text-12 text-ink-muted leading-relaxed">
              The commit would answer exactly the same way, so it stays closed. Nothing has been
              written.
            </div>
          </div>
        )}

        {/* THE COMMIT RESPONSE IS THE ROW ALONE. RecordPayrollPeriodOutcome
            and its restate twin both carry a Deductions field internally, but
            the contract is explicit that "only Row rides the wire to the
            client" — the deliberate ruling that old-beside-new belongs to the
            PREVIEW, not to the write. PayrollPeriodResult therefore has no
            deductions and no net, and the TypeScript type reflects that, so
            nothing here can accidentally render a resolved set the response
            never contained.
            The consequence is real and is handled rather than papered over:
            after a correction that INHERITED its deductions, this panel does
            not know what was written. onCorrected triggers the pay run's
            refetch, and the refreshed table — which reads PayrollLedgerRow,
            where deductions and netPence do ride the wire — is where the
            resolved set becomes visible. The sentence below says so instead of
            implying this panel already knows. */}
        {committed && (
          <div className="text-13 text-ink-primary leading-relaxed">
            {committed.replayed ? (
              <>Already applied — this correction had been made, and the row below is the one it
              produced. Nothing was superseded a second time.</>
            ) : (
              <>Corrected — period {committed.row.periodNumber} now stands at{' '}
              {money(committed.row.grossPence / 100)}. The previous row is kept as superseded
              history. The pay run has been refreshed: the replacement's deductions and net are
              shown there, read back from the stored row rather than assumed from this form.</>
            )}
          </div>
        )}

        {/* ── Step 2: old, new, and what changes ─────────────────────────── */}
        {preview && (
          <div className="flex flex-col gap-3 pt-3 border-t border-edge-row-faint">
            {alreadyRestated ? (
              <div className="text-13 text-ink-primary leading-relaxed">
                <span className="font-semibold">Already corrected.</span>{' '}
                This correction has already been made — the replacement is shown below. Making it
                again cannot change anything.
              </div>
            ) : (
              <div className="text-12 text-ink-muted leading-relaxed">
                This would supersede the current row for tax year {preview.row.taxYear}, period{' '}
                {preview.row.periodNumber}.
              </div>
            )}

            <div className="grid grid-cols-[max-content_1fr_1fr] gap-x-6 gap-y-2">
              <div />
              <div className="k-eyebrow uppercase">Current (would be superseded)</div>
              <div className="k-eyebrow uppercase">Replacement</div>
              {comparisons(preview.current, preview.row).map(c => (
                <Compare key={c.label} label={c.label} before={c.before} after={c.after} />
              ))}
            </div>

            {/* THE RESOLVED SET, side by side — the standing condition that
                keeps inheritance from being silent. `preview.deductions` is
                what the replacement WILL carry, resolved server-side: when the
                request omitted the field, this is the INHERITED list, not an
                empty space. So the operator sees the actual outcome of the
                tri-state rather than the instruction they gave.
                `preview.currentDeductions` is the superseded row's list. Both
                come from the server; neither is the form state. */}
            <div className="grid grid-cols-[max-content_1fr_1fr] gap-x-6 gap-y-2 pt-3 border-t border-edge-row-faint">
              <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted self-start">Deductions</div>
              <div><DeductionList deductions={preview.currentDeductions} /></div>
              <div>
                <DeductionList deductions={preview.deductions} />
                {!deductionsTouched && preview.deductions.length > 0 && (
                  <div className="text-12 text-ink-muted mt-1 leading-relaxed">
                    Inherited from the row being superseded.
                  </div>
                )}
              </div>
            </div>

            {/* THE RESOLVED HOURS, side by side — the same pin that keeps
                deduction inheritance from being silent. preview.hours is what
                the replacement WILL carry (inherited when the request omitted
                the field); preview.currentHours is the superseded row's.
                Both absent means the payday's declaration is false. */}
            {preview.hours !== undefined && (
              <div className="grid grid-cols-[max-content_1fr_1fr] gap-x-6 gap-y-2 pt-3 border-t border-edge-row-faint">
                <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted self-start">Hours</div>
                <div><HoursList hours={preview.currentHours ?? []} /></div>
                <div>
                  <HoursList hours={preview.hours} />
                  {!hoursTouched && preview.hours.length > 0 && (
                    <div className="text-12 text-ink-muted mt-1 leading-relaxed">
                      Inherited from the row being superseded.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* The explicit diff: an operator should not have to compare two
                columns by eye to learn what a permanent write changes. */}
            <div className="flex flex-col gap-1 pt-3 border-t border-edge-row-faint">
              <div className="k-eyebrow uppercase">What changes</div>
              {changes.length === 0 ? (
                <div className="text-13 text-ink-secondary leading-relaxed">
                  No figure changes — the replacement is identical to the current row. Committing
                  would still supersede it, recording the new reason against the same figures.
                </div>
              ) : (
                <ul className="text-13 text-ink-primary leading-relaxed">
                  {changes.map(c => (
                    <li key={c.label} className="tabular-nums">
                      {c.label}: <span className="text-ink-secondary">{c.before}</span> → <span className="font-semibold">{c.after}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-4 gap-x-4 gap-y-3 pt-3 border-t border-edge-row-faint">
              <Figure label="Chain depth"
                value={preview.chainDepth === 1 ? 'First correction' : `Correction ${preview.chainDepth}`} muted />
              <Figure label="Prior pay to date"
                value={preview.derivedPriorPayPence === undefined ? '—' : signedMoney(preview.derivedPriorPayPence)} muted />
              <Figure label="Prior tax to date"
                value={preview.derivedPriorTaxPence === undefined ? '—' : signedMoney(preview.derivedPriorTaxPence)} muted />
              <Figure label="Frequency" value={preview.row.payFrequency} muted />
            </div>

            <div className="text-11 text-ink-muted leading-relaxed break-all">
              Priors are summed over the periods BEFORE this one — the row being replaced is not
              among them. Profile version {preview.row.profileId} · engine {preview.engineIdentity}
            </div>

            {/* ── Step 4: the typed confirmation, then the commit ─────────── */}
            {!alreadyRestated && (
              <div className="flex flex-col gap-2 pt-3 border-t border-edge-row-faint">
                <label htmlFor="cor-confirm" className={labelCls}>
                  Type “{CONFIRM_WORD}” to confirm
                </label>
                <input id="cor-confirm" value={confirmText} autoComplete="off"
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_WORD}
                  className={`${inputCls} max-w-xs`} />
                <div className="text-12 text-ink-muted leading-relaxed">
                  This replaces the period’s current figure permanently. There is no undo — a
                  mistake here can only be answered with another correction.
                </div>
                <div>
                  <button onClick={commit} disabled={commitDisabled}
                    className="k-btn k-btn--lg inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                    <Check size={14} /> {busy === 'commit' ? 'Correcting…' : `Supersede period ${preview.row.periodNumber}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
